import React, { useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react'
import * as Blockly from 'blockly'
import { javascriptGenerator } from 'blockly/javascript'
import { ensureBlocklySetup } from './blocklySetup'

export interface BlocklyWorkspaceHandle {
  getGeneratedCode: () => string
}

interface BlocklyWorkspaceProps {
  allowedCategories?: string[]
  disabled?: boolean
  initialXml?: string
}

const CATEGORY_COLOURS: Record<string, number> = {
  Logic: 210,
  Loops: 120,
  Math: 230,
  Text: 160,
  Lists: 260,
  Variables: 330,
  Functions: 290,
}

const CATEGORY_BLOCKS: Record<string, string> = {
  Logic: `
    <block type="controls_if"></block>
    <block type="controls_ifelse"></block>
    <block type="logic_compare"></block>
    <block type="logic_operation"></block>
    <block type="logic_negate"></block>
    <block type="logic_boolean"></block>
    <block type="logic_ternary"></block>`,
  Loops: `
    <block type="controls_repeat_ext"><value name="TIMES"><shadow type="math_number"><field name="NUM">10</field></shadow></value></block>
    <block type="controls_whileUntil"></block>
    <block type="controls_for"><value name="FROM"><shadow type="math_number"><field name="NUM">1</field></shadow></value><value name="TO"><shadow type="math_number"><field name="NUM">10</field></shadow></value><value name="BY"><shadow type="math_number"><field name="NUM">1</field></shadow></value></block>
    <block type="controls_forEach"></block>
    <block type="controls_flow_statements"></block>`,
  Math: `
    <block type="math_number"><field name="NUM">0</field></block>
    <block type="math_arithmetic"></block>
    <block type="math_single"></block>
    <block type="math_trig"></block>
    <block type="math_constant"></block>
    <block type="math_number_property"></block>
    <block type="math_round"></block>
    <block type="math_modulo"></block>
    <block type="math_random_int"><value name="FROM"><shadow type="math_number"><field name="NUM">1</field></shadow></value><value name="TO"><shadow type="math_number"><field name="NUM">100</field></shadow></value></block>
    <block type="math_random_float"></block>`,
  Text: `
    <block type="text"></block>
    <block type="text_join"></block>
    <block type="text_append"><value name="TEXT"><shadow type="text"></shadow></value></block>
    <block type="text_length"></block>
    <block type="text_isEmpty"></block>
    <block type="text_indexOf"></block>
    <block type="text_charAt"></block>
    <block type="text_getSubstring"></block>
    <block type="text_changeCase"></block>
    <block type="text_trim"></block>
    <block type="text_print"></block>`,
  Lists: `
    <block type="lists_create_empty"></block>
    <block type="lists_create_with"></block>
    <block type="lists_repeat"><value name="NUM"><shadow type="math_number"><field name="NUM">5</field></shadow></value></block>
    <block type="lists_length"></block>
    <block type="lists_isEmpty"></block>
    <block type="lists_indexOf"></block>
    <block type="lists_getIndex"></block>
    <block type="lists_setIndex"></block>
    <block type="lists_getSublist"></block>
    <block type="lists_sort"></block>`,
}

ensureBlocklySetup()

function buildToolboxXml(categories: string[]): string {
  const cats = categories.map((name) => {
    const colour = CATEGORY_COLOURS[name] ?? 200
    if (name === 'Variables') {
      return `<category name="${name}" colour="${colour}" custom="VARIABLE"></category>`
    }
    if (name === 'Functions') {
      return `<category name="${name}" colour="${colour}" custom="PROCEDURE"></category>`
    }
    const blocks = CATEGORY_BLOCKS[name] || ''
    return `<category name="${name}" colour="${colour}">${blocks}</category>`
  })
  return `<xml xmlns="https://developers.google.com/blockly/xml">
    ${cats.join('\n')}
  </xml>`
}

const BlocklyWorkspace = forwardRef<BlocklyWorkspaceHandle, BlocklyWorkspaceProps>(
  ({ allowedCategories = ['Logic', 'Loops', 'Math', 'Text', 'Variables', 'Functions'], disabled = false, initialXml }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const workspaceRef = useRef<Blockly.WorkspaceSvg | null>(null)

    const getGeneratedCode = useCallback((): string => {
      if (!workspaceRef.current) return ''
      return javascriptGenerator.workspaceToCode(workspaceRef.current)
    }, [])

    useImperativeHandle(ref, () => ({ getGeneratedCode }), [getGeneratedCode])

    useEffect(() => {
      if (!containerRef.current) return

      const toolboxXml = buildToolboxXml(allowedCategories)

      const workspace = Blockly.inject(containerRef.current, {
        toolbox: toolboxXml,
        scrollbars: true,
        trashcan: true,
        move: { scrollbars: true, drag: true, wheel: true },
        zoom: { controls: true, wheel: false, startScale: 1.0, maxScale: 3, minScale: 0.3 },
        grid: { spacing: 20, length: 3, colour: '#ccc', snap: true },
        readOnly: disabled,
      })

      workspaceRef.current = workspace

      if (initialXml) {
        try {
          const dom = Blockly.utils.xml.textToDom(initialXml)
          Blockly.Xml.domToWorkspace(dom, workspace)
        } catch {
          // ignore malformed xml
        }
      }

      // Add start block if workspace doesn't already have one
      if (!workspace.getBlocksByType('start_block', false).length) {
        const startBlock = workspace.newBlock('start_block')
        startBlock.initSvg()
        startBlock.render()
        startBlock.moveBy(30, 30)
      }

      return () => {
        workspace.dispose()
        workspaceRef.current = null
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <>
        <style>{`.blocklyFlyoutScrollbar { display: none !important; } .blocklyTreeLabel, .blocklyToolboxCategory .blocklyTreeLabel, .blocklyTreeRow .blocklyTreeLabel { color: #000 !important; }`}</style>
        <div className="w-full h-[400px] border border-gray-200 rounded-lg overflow-hidden">
          <div
            ref={containerRef}
            className="w-full h-full"
          />
        </div>
      </>
    )
  }
)

BlocklyWorkspace.displayName = 'BlocklyWorkspace'
export default BlocklyWorkspace
